import { useCallback, useMemo, useState } from "react";
import { canFinanceWrite } from "helpers/financePermissions";
import { useAccess } from "hooks/useAccess";
import {
  tituloViewParaForm,
  validarFormContaPagar,
} from "../helpers/conta-pagar-form-payload";
import { atualizarContaPagar } from "../services/contas-pagar.service";
import type { ContaPagarView } from "../types/contas-pagar.types";
import {
  formPadraoNovaContaPagar,
  type NovaContaPagarForm,
} from "./useCriarTituloPagar";

export type EditarTituloPagarFeedback =
  | { severity: "success"; message: string }
  | { severity: "error"; message: string }
  | null;

export type UseEditarTituloPagarOptions = {
  onSuccess?: () => void;
};

export function podeEditarTituloPagar(row: ContaPagarView): boolean {
  if (row.status.toUpperCase() !== "PENDENTE") return false;
  if (row.valorPago > 0) return false;
  if (row.valorAberto <= 0) return false;
  return true;
}

export function useEditarTituloPagar(options?: UseEditarTituloPagarOptions) {
  const access = useAccess();
  const [open, setOpen] = useState(false);
  const [tituloId, setTituloId] = useState<string | null>(null);
  const [form, setForm] = useState<NovaContaPagarForm>(() =>
    formPadraoNovaContaPagar(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<EditarTituloPagarFeedback>(null);

  const podeEditar = useMemo(() => canFinanceWrite(access), [access]);

  const abrir = useCallback((titulo: ContaPagarView) => {
    if (!podeEditarTituloPagar(titulo)) return;
    setTituloId(titulo.id);
    setForm(tituloViewParaForm(titulo));
    setError(null);
    setOpen(true);
  }, []);

  const fechar = useCallback(() => {
    if (loading) return;
    setOpen(false);
    setTituloId(null);
    setError(null);
  }, [loading]);

  const clearFeedback = useCallback(() => setFeedback(null), []);

  const confirmar = useCallback(async () => {
    if (!tituloId) return;
    setError(null);
    const validado = validarFormContaPagar(form);
    if (validado.ok === false) {
      setError(validado.error);
      return;
    }

    setLoading(true);
    try {
      const { status, data, error: apiError } = await atualizarContaPagar(
        tituloId,
        validado.payload,
      );
      if (status === 200 && data?.id) {
        setOpen(false);
        setTituloId(null);
        setFeedback({
          severity: "success",
          message: "Título atualizado com sucesso.",
        });
        options?.onSuccess?.();
        return;
      }
      setError(
        apiError ?? "Não foi possível atualizar o título. Verifique os dados.",
      );
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Falha ao atualizar título a pagar.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [form, tituloId, options]);

  return {
    open,
    form,
    setForm,
    loading,
    error,
    feedback,
    podeEditar,
    abrir,
    fechar,
    confirmar,
    clearFeedback,
  };
}
